/**
 * Core Web Vitals → GA4 reporter.
 *
 * Sends LCP, INP, CLS, FCP, and TTFB to GA4 as `web_vitals` events with the
 * recommended parameter shape (see
 * https://github.com/GoogleChrome/web-vitals#send-the-results-to-google-analytics).
 *
 * - Values are converted to integers (CLS × 1000) so GA4 can aggregate them.
 * - Each event includes `metric_id` so you can group multiple deltas from the
 *   same page lifecycle in BigQuery / Looker Studio.
 * - Uses `non_interaction: true` so vitals don't inflate engagement metrics.
 * - Honors GA4 Consent Mode — if `analytics_storage` is denied, the event is
 *   queued by gtag and only fires after consent is granted.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { trackEvent } from "./analytics";

const sendToGA = (metric: Metric) => {
  // CLS is unitless and small — multiply so GA4 sees an integer.
  const value = Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value);

  trackEvent("web_vitals", {
    metric_name: metric.name,
    metric_id: metric.id,
    metric_value: value,
    metric_delta: Math.round(
      metric.name === "CLS" ? metric.delta * 1000 : metric.delta,
    ),
    metric_rating: metric.rating, // "good" | "needs-improvement" | "poor"
    metric_navigation_type: metric.navigationType,
    // Mirror the metric value into GA4's standard `value` parameter so it
    // works with default reports.
    value,
    non_interaction: true,
  });
};

let started = false;

/**
 * Start observing Core Web Vitals and forward them to GA4. Idempotent.
 */
export const initWebVitals = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  // Defer until idle so vitals collection never delays hydration / TTI.
  const start = () => {
    onLCP(sendToGA);
    onINP(sendToGA);
    onCLS(sendToGA);
    onFCP(sendToGA);
    onTTFB(sendToGA);
  };

  if ("requestIdleCallback" in window) {
    (window as Window & {
      requestIdleCallback: (cb: () => void) => void;
    }).requestIdleCallback(start);
  } else {
    setTimeout(start, 0);
  }
};